import { z } from 'zod';

import { AuthenticatorId, AuthenticatorType, WebauthnId } from 'backend-client-web';
import {
  RawSigningPublicKey,
  RawEncryptionPublicKey,
  EncryptionPublicKeySignature,
  SigningPublicKeySignature,
  loadRawSigningPublicKey,
  loadRawEncryptionPublicKey,
  loadEncryptionPublicKeySignature,
  loadSigningPublicKeySignature,
} from 'vault-crypto';

import { brandedUint8Array } from '../../brands';
import { makeProtobufEnumSchema } from '../../enumSchemas';
import { uint8ArraySchema } from '../../uint8ArraySchema';
import { WithUnknownValuePlaceholder } from '../../unknownValuePlaceholder';

/**
 * WARNING! The types defined in this file are used by ClientCoreParameters and can only be changed
 * in backwards compatible ways! See ClientCoreParameters.ts for details.
 */

interface AuthenticatorWebauthn {
  webauthnId: WebauthnId;
  prfSalt: Uint8Array;
}

const AuthenticatorWebauthn = z.object({
  webauthnId: z.string(),
  prfSalt: uint8ArraySchema,
});

interface PersistableAuthenticator {
  readonly id: AuthenticatorId;
  readonly authenticatorType: WithUnknownValuePlaceholder<AuthenticatorType>;
  readonly highSecurityLoginSigPubKey: RawSigningPublicKey;
  readonly highSecurityIdentitySigPubKey: RawSigningPublicKey;
  readonly highSecurityProfileSeedEncPubKey: RawEncryptionPublicKey;
  readonly highSecurityProfileSeedEncPubKeySignature: EncryptionPublicKeySignature;
  readonly storableSigPubKey: RawSigningPublicKey;
  readonly storableSigPubKeySignature: SigningPublicKeySignature;
  readonly storableProfileSeedEncPubKey: RawEncryptionPublicKey;
  readonly storableProfileSeedEncPubKeySignature: EncryptionPublicKeySignature;
  readonly secretInfo: string;
  readonly secretSalt: Uint8Array;
  readonly webauthn?: AuthenticatorWebauthn;
}

export function deviceTypeToAuthenticatorType(deviceType: string): AuthenticatorType {
  switch (deviceType) {
    case 'push':
      return AuthenticatorType.PUSH;
    case 'recovery':
      return AuthenticatorType.BACKUP_CODE;
    case 'backup':
      return AuthenticatorType.BACKUP_OS;
    case 'dummy':
      return AuthenticatorType.DUMMY;
    default:
      return AuthenticatorType.UNKNOWN;
  }
}

const AuthenticatorKeyNamesV4 = z.object({
  id: z.uuid(),
  authenticatorType: makeProtobufEnumSchema(AuthenticatorType, AuthenticatorType.UNKNOWN),
  highSecurityLoginSigPubKey: brandedUint8Array(loadRawSigningPublicKey),
  highSecurityIdentitySigPubKey: brandedUint8Array(loadRawSigningPublicKey),
  highSecurityProfileSeedEncPubKey: brandedUint8Array(loadRawEncryptionPublicKey),
  highSecurityProfileSeedEncPubKeySignature: brandedUint8Array(loadEncryptionPublicKeySignature),
  storableSigPubKey: brandedUint8Array(loadRawSigningPublicKey),
  storableSigPubKeySignature: brandedUint8Array(loadSigningPublicKeySignature),
  storableProfileSeedEncPubKey: brandedUint8Array(loadRawEncryptionPublicKey),
  storableProfileSeedEncPubKeySignature: brandedUint8Array(loadEncryptionPublicKeySignature),
  secretInfo: z.string(),
  secretSalt: uint8ArraySchema,
  webauthn: AuthenticatorWebauthn.optional(),
});

const AuthenticatorKeyNamesV3 = z
  .object({
    id: z.uuid(),
    authenticatorType: makeProtobufEnumSchema(AuthenticatorType, AuthenticatorType.UNKNOWN),
    highSecurityLoginSigPubKey: brandedUint8Array(loadRawSigningPublicKey),
    highSecurityIdentitySigPubKey: brandedUint8Array(loadRawSigningPublicKey),
    highSecurityVaultKeyEncPubKey: brandedUint8Array(loadRawEncryptionPublicKey),
    highSecurityVaultKeyEncPubKeySignature: brandedUint8Array(loadEncryptionPublicKeySignature),
    storableSigPubKey: brandedUint8Array(loadRawSigningPublicKey),
    storableSigPubKeySignature: brandedUint8Array(loadSigningPublicKeySignature),
    storableVaultKeyEncPubKey: brandedUint8Array(loadRawEncryptionPublicKey),
    storableVaultKeyEncPubKeySignature: brandedUint8Array(loadEncryptionPublicKeySignature),
    secretInfo: z.string(),
    secretSalt: z.instanceof(Uint8Array),
    webauthn: AuthenticatorWebauthn.optional(),
  })
  .transform(
    ({
      storableVaultKeyEncPubKey,
      storableVaultKeyEncPubKeySignature,
      highSecurityVaultKeyEncPubKey,
      highSecurityVaultKeyEncPubKeySignature,
      ...rest
    }) => ({
      storableProfileSeedEncPubKey: storableVaultKeyEncPubKey,
      storableProfileSeedEncPubKeySignature: storableVaultKeyEncPubKeySignature,
      highSecurityProfileSeedEncPubKey: highSecurityVaultKeyEncPubKey,
      highSecurityProfileSeedEncPubKeySignature: highSecurityVaultKeyEncPubKeySignature,
      ...rest,
    }),
  );

const AuthenticatorKeyNamesV2 = z
  .object({
    id: z.uuid(),
    deviceType: z.string(),
    highSecurityLoginSigPubKey: brandedUint8Array(loadRawSigningPublicKey),
    highSecurityIdentitySigPubKey: brandedUint8Array(loadRawSigningPublicKey),
    highSecurityVaultKeyEncPubKey: brandedUint8Array(loadRawEncryptionPublicKey),
    highSecurityVaultKeyEncPubKeySignature: brandedUint8Array(loadEncryptionPublicKeySignature),
    storableSigPubKey: brandedUint8Array(loadRawSigningPublicKey),
    storableSigPubKeySignature: brandedUint8Array(loadSigningPublicKeySignature),
    storableVaultKeyEncPubKey: brandedUint8Array(loadRawEncryptionPublicKey),
    storableVaultKeyEncPubKeySignature: brandedUint8Array(loadEncryptionPublicKeySignature),
    secretInfo: z.string(),
    secretSalt: uint8ArraySchema,
  })
  .transform(
    ({
      storableVaultKeyEncPubKey,
      storableVaultKeyEncPubKeySignature,
      highSecurityVaultKeyEncPubKey,
      highSecurityVaultKeyEncPubKeySignature,
      deviceType,
      ...rest
    }) => ({
      storableProfileSeedEncPubKey: storableVaultKeyEncPubKey,
      storableProfileSeedEncPubKeySignature: storableVaultKeyEncPubKeySignature,
      highSecurityProfileSeedEncPubKey: highSecurityVaultKeyEncPubKey,
      highSecurityProfileSeedEncPubKeySignature: highSecurityVaultKeyEncPubKeySignature,
      authenticatorType: deviceTypeToAuthenticatorType(deviceType),
      ...rest,
    }),
  );

const AuthenticatorKeyNamesV1 = z
  .object({
    id: z.uuid(),
    deviceType: z.string(),
    loginSigPubKey: brandedUint8Array(loadRawSigningPublicKey),
    highSecuritySigPubKey: brandedUint8Array(loadRawSigningPublicKey),
    highSecurityEncPubKey: brandedUint8Array(loadRawEncryptionPublicKey),
    highSecurityEncPubKeySignature: brandedUint8Array(loadEncryptionPublicKeySignature),
    sigPubKey: brandedUint8Array(loadRawSigningPublicKey),
    sigPubKeySignature: brandedUint8Array(loadSigningPublicKeySignature),
    encPubKey: brandedUint8Array(loadRawEncryptionPublicKey),
    encPubKeySignature: brandedUint8Array(loadEncryptionPublicKeySignature),
    secretInfo: z.string(),
    secretSalt: uint8ArraySchema,
  })
  .transform(
    ({
      deviceType,
      loginSigPubKey,
      highSecuritySigPubKey,
      highSecurityEncPubKey,
      highSecurityEncPubKeySignature,
      sigPubKey,
      sigPubKeySignature,
      encPubKey,
      encPubKeySignature,
      ...rest
    }) => ({
      authenticatorType: deviceTypeToAuthenticatorType(deviceType),
      highSecurityLoginSigPubKey: loginSigPubKey,
      highSecurityIdentitySigPubKey: highSecuritySigPubKey,
      highSecurityProfileSeedEncPubKey: highSecurityEncPubKey,
      highSecurityProfileSeedEncPubKeySignature: highSecurityEncPubKeySignature,
      storableSigPubKey: sigPubKey,
      storableSigPubKeySignature: sigPubKeySignature,
      storableProfileSeedEncPubKey: encPubKey,
      storableProfileSeedEncPubKeySignature: encPubKeySignature,
      ...rest,
    }),
  );

const PersistableAuthenticator = z.union([
  AuthenticatorKeyNamesV1,
  AuthenticatorKeyNamesV2,
  AuthenticatorKeyNamesV3,
  AuthenticatorKeyNamesV4,
]);

export { PersistableAuthenticator };
